import Header from './header/header';
import {
    screenshotModeEnabled,
    trimValue,
    parseJsonAttribute,
    parseEasingFunctionParameter,
} from '../utilities/utilities.js';

/**
 * Return value for getScrollTargetForElement and getScrollTargetForSelector
 * @typedef {object} ScrollTargetInfo
 * @property {number} position
 * @property {number} jump
 */

const settings = {
    cssProperties: {
        // How far <main> has been offset from its natural position
        mainOffset: '--scroll-lock-offset',

        // How far along we are in the scroll animation, from 0 to 1
        progress: '--scroll-lock-progress',
    },

    classNames: {
        mainContainerScrollLock: 'scroll-lock',
    },

    defaultParameters: {
        // Easing applied to the "--scroll-lock-progress" CSS property;
        // this easing isn't applied to "this.progress" so that javascript
        // based animation can supply its own easings as needed - for
        // example, this easing affects the image zoom-outs on internal
        // pages, but not the GSAP animation that drives the circle
        // screen transitions
        cssEasing: 'power1.out',

        // Distance in which the scroll speed slows down prior to entering
        // the scroll locked state and speeding up after exiting the
        // scroll locked state, in visual pixels - this gets extended in
        // order to achieve the effect. When set to 0, scrolling starts and stops
        // immediately when users enter and leave the region where
        // scrolling is locked
        accelerationDistance: 200,

        // The strength of the acceleration curve:
        //  2 = quadratic (x^2 or GSAP "power1")
        //  3 = cubic (x^3 or GSAP "power2")
        //  4 = quartic (x^4 or GSAP "power3")
        accelerationFactor: 3,

        // The amount which the scroll distance occupied by acceleration
        // overlaps with the scroll distance of the animation; this is nice
        // because these animations tend to ease in and out to begin with,
        // and stretching out the scroll distance leading into and out of
        // that can make things feel sluggish. Similarly, setting this to
        // "1" makes the animations start zipping along before scroll
        // has even begun... all of these values should be configurable
        // via the "data-scroll-lock" HTML attribute if there are specific
        // instances where using something other than these defaults would
        // be beneficial
        //  0 = No overlap, and the animation only plays in between accelerations
        //  1 = Total overlap, and the animation plays during accelerations
        decelerationOverlap: 0.875,
        accelerationOverlap: 0.875,

        // When set to 'center', the animation's containing element will
        // lock in place when centered in the viewport if there's enough
        // vertical space to do so; otherwise, the element will lock at
        // the top of the viewport
        pinning: 'center',
    },

    focusMonitoring: {
        skipTagNames: [
            'HTML',
            'BODY',
            'VIDEO',
        ],

        scrollPadding: 50,
    },
};

// Tracks the scroll lock animations present on the page
const scrollLockAnimations = [];

// Observers requesting notifications when the scroll position changes
const scrollPositionChangeCallbacks = [];
let scrollPositionChangeCallbacksInitializationTimeout = null;

// The current offset of the <main> container to achieve scrolling
let mainOffset = 0;

/** Base class for scroll-locked sections */
export default class ScrollLockAnimation {
    /**
     * @constructor
     * @param {HTMLElement} container The container that contains the
     * scroll-locked animation
     * @param {number} scrollLockDistance The distance through
     * which the user must scroll to complete the animation
     */
    constructor(
        container,
        scrollLockDistance,
    ) {
        this.container = container;
        this.scrollLockDistance = scrollLockDistance;
        this.progress = 0;

        // Parse any animation parameters provided via JSON in an HTML attribute
        this.parameters = Object.assign(
            {...settings.defaultParameters},
            parseJsonAttribute(
                this.container,
                'data-scroll-lock',
            ),
        );

        this.cssEasingFunction = parseEasingFunctionParameter(
            this.parameters.cssEasing,
        );

        // The underpinnings of these are quartic (x^4)
        this.decelerateFunction = parseEasingFunctionParameter(`power${this.parameters.accelerationFactor - 1}.out`);
        this.accelerateFunction = parseEasingFunctionParameter(`power${this.parameters.accelerationFactor - 1}.in`);

        this.parameters.accelerationDistance =
            this.parameters.accelerationDistance || 0;
        if (this.parameters.accelerationDistance < 0) {
            this.parameters.accelerationDistance = 0;
        }

        // The range our container takes up within <main> - these values are
        // established in the call to "initializeAnimations" below
        this.mainContainerRange = {
            top: 0,
            bottom: 0,
        };

        // Acceleration & deceleration:
        // The distance required to accelerate to a normal scroll velocity is
        // the derivative of the acceleration function; cubic (x^3 or "power2")
        // will require 3 times the distance, for example
        this.accelerationVisualDistance = this.parameters.accelerationDistance;
        this.accelerationScrollDistance = (
            this.parameters.accelerationDistance *
            this.parameters.accelerationFactor
        );

        // The range at which scroll locking is in effect for this component -
        // these values are established in the call to "initializeAnimations" below
        this.scrollLockRange = {
            decelerateStart: 0,
            decelerateEnd: 0,
            decelerateDistance: 0,

            animationStart: 0,
            animationEnd: 0,
            animationDistance: this.scrollLockDistance,

            accelerateStart: 0,
            accelerateEnd: 0,
            accelerateDistance: 0,

            totalScrollAdjustments: this.scrollLockDistance,
        };

        // Record this instance
        scrollLockAnimations.push(this);

        // If this is the first scroll locking animation added to the page,
        // perform our general initialization
        if (scrollLockAnimations.length === 1) {
            initializeAnimations();
        }
    }

    static get scrollLockAnimationsPresent() {
        return scrollLockAnimations.length > 0;
    }

    /**
     * Determines the scroll target position for a given element
     * on the page, as altered by ScrollLockAnimation objects
     * @param {Element} element
     * @return {ScrollTargetInfo}
     */
    static getScrollTargetForElement(element) {
        // Check if this element is located inside of <main>
        const elementBounds = element.getBoundingClientRect();
        const elementTop = Math.round(elementBounds.top);
        const mainContainer = element.closest('main');
        if (mainContainer) {
            // This element is inside of <main> - translate its location
            // to the location withing the overall scroll range
            const mainContainerBounds = mainContainer.getBoundingClientRect();
            const mainContainerTop = Math.round(mainContainerBounds.top);

            const distanceIntoMainContainer = elementTop - mainContainerTop;
            let scrollAdjustment = 0;
            for (let i=0; i<scrollLockAnimations.length; i++) {
                const scrollLockAnimation = scrollLockAnimations[i];
                const mainContainerRange = scrollLockAnimation
                    .mainContainerRange;

                if (distanceIntoMainContainer < mainContainerRange.top) {
                    // Element is before this animation
                    return {
                        position: mainContainerTop +
                            scrollAdjustment +
                            distanceIntoMainContainer +
                            mainOffset,
                        jump: scrollAdjustment,
                    };
                }

                if (distanceIntoMainContainer < mainContainerRange.bottom) {
                    // Element is inside this animation
                    return {
                        position: mainContainerTop +
                            scrollAdjustment +
                            mainContainerRange.top +
                            mainOffset,
                        jump: scrollAdjustment,
                    };
                }

                // Element is after this animation
                scrollAdjustment += scrollLockAnimation
                    .scrollLockRange
                    .totalScrollAdjustments;
            }

            // Element is after all the animations
            return {
                position: mainContainerTop +
                    scrollAdjustment +
                    distanceIntoMainContainer +
                    mainOffset,
                jump: scrollAdjustment,
            };
        } else {
            // This element isn't inside of <main> - just return its
            // actual location on the page
            return {
                position: elementTop + window.pageYOffset,
                jump: 0,
            };
        }
    }

    /**
     * Used for anchor link scrolling logic in anchor-links.js
     * @param {string} selector
     * @return {ScrollTargetInfo}
     */
    static getScrollTargetForSelector(selector) {
        // First, find the position of the element on the page as a whole
        const element = document.querySelector(selector);
        if (!element) {
            return null;
        }

        return this.getScrollTargetForElement(element);
    }

    /**
     * Registers a callback to be performed whenever the scroll
     * position changes - we do this so that there is tight control
     * over updates made during scrolling. If each scroll-based
     * component had their own requestAnimationFrame loop (or maybe
     * worse, window scroll event listeners), there might be a
     * slight lag that will cause things to not display in sync
     * as the user scrolls
     * @param {function} callback
     */
    static addScrollPositionChangeCallback(callback) {
        if (scrollPositionChangeCallbacks.includes(callback)) {
            return;
        }

        // Register this observer
        scrollPositionChangeCallbacks.push(callback);

        if (scrollPositionChangeCallbacksInitializationTimeout == null) {
            scrollPositionChangeCallbacksInitializationTimeout =
                setTimeout(
                    () => {
                        // If there are no scroll locking elements on
                        // the page, just implement this using window
                        // scroll events
                        window.addEventListener('scroll', () => {
                            scrollPositionChangeCallbacks
                                .forEach((callback) => {
                                    callback(window.pageYOffset);
                                });
                        });
                    },
                    1,
                );
        }
    }

    /**
     * Signals that we should update our position within the animation;
     * "progress" indicates how far the user has scrolled through
     * the animation when the container is fully onscreen.
     * This should be overrided in derived classes
     * @param {number} progress
     */
    setAnimationProgress(progress) {
        if (progress == this.progress) {
            return;
        }

        // Store raw value
        this.progress = progress;

        // Apply our parameters
        let adjustedValue = applyParameters.call(
            this,
            progress,
        );

        // Update CSS properties
        adjustedValue = trimValue(adjustedValue);

        this.container.style.setProperty(
            settings.cssProperties.progress,
            `${adjustedValue}`,
        );
    }
}

/*
    Internal functions
*/

function initializeAnimations() {
    // Add our scroll lock class name to the <main> container
    const mainContainer = document.querySelector('main');
    mainContainer.classList.add(settings.classNames.mainContainerScrollLock);

    // Check if screenshot mode is enabled
    if (screenshotModeEnabled) {
        // Stop here
        return;
    }

    // Add a spacing element to establish the height of the page
    // now that <main> has switched to fixed positioning
    const spacer = document.createElement('div');
    mainContainer.parentNode.insertBefore(spacer, mainContainer);

    // Setup our tick function to monitor the current scroll position
    // and changes to the content dimensions - setting this value to "null"
    // will force an update on the first tick call
    let mainContainerWidth = 0;
    let mainContainerHeight = 0;
    let scrollPosition = null;
    const tick = () => {
        // Check if there has been a change to the content dimensions,
        // and recalculate scroll ranges if so
        let viewportDimensionsChanged = false;
        if (
            mainContainer.clientWidth != mainContainerWidth ||
            mainContainer.clientHeight != mainContainerHeight
        ) {
            // Flag that our viewport dimensions have changed
            viewportDimensionsChanged = true;

            // Record new values
            mainContainerWidth = mainContainer.clientWidth;
            mainContainerHeight = mainContainer.clientHeight;

            // Recalculate the scroll positions where each of our components
            // begin scroll locking, and calculate the total added scroll distance
            // required by all our scroll locked components
            const mainContainerBounds = mainContainer.getBoundingClientRect();
            let accumulatedScrollDistance = 0;
            scrollLockAnimations.forEach((scrollLockAnimation) => {
                calculateRanges.call(
                    scrollLockAnimation,
                    mainContainerBounds,
                    accumulatedScrollDistance,
                );

                accumulatedScrollDistance += scrollLockAnimation
                    .scrollLockRange
                    .totalScrollAdjustments;
            });

            // Adjust size of spacer
            spacer.style.height = `${mainContainerHeight + accumulatedScrollDistance}px`;
        }

        // Check if there has been a change to the scroll position
        let scrollPositionChanged = false;
        if (window.pageYOffset !== scrollPosition) {
            // Flag that our scroll position changed
            scrollPositionChanged = true;

            // Record new value
            scrollPosition = window.pageYOffset;
        }

        // Check if our state has changed
        if (viewportDimensionsChanged || scrollPositionChanged) {
            // Our state has changed - determine what our <main> offset should be
            // based on the current scroll position and update our animations
            let newMainOffset = scrollPosition;
            scrollLockAnimations.forEach((scrollLockAnimation) => {
                // Check the location of our container with respect to the current
                // scroll position and determine how it should affect how much we
                // offset the <main> element
                const scrollLockRange = scrollLockAnimation.scrollLockRange;
                if (scrollPosition < scrollLockRange.decelerateStart) {
                    // We're not yet in our component scroll range -
                    // nothing to do here
                } else if (scrollPosition < scrollLockRange.decelerateEnd) {
                    // We're in deceleration range - adjust the <main> offset
                    // to reflect how far we've progressed through it, starting
                    // with removing the raw scroll distance into this range
                    const rawScrollDistance =
                        scrollPosition -
                        scrollLockRange.decelerateStart;

                    newMainOffset -= rawScrollDistance;

                    // Now add back the eased distance
                    const percentComplete =
                        rawScrollDistance /
                        scrollLockRange.decelerateDistance;

                    const easedPercentComplete = scrollLockAnimation
                        .decelerateFunction(percentComplete);

                    const easedScrollDistance =
                        scrollLockAnimation.accelerationVisualDistance *
                        easedPercentComplete;

                    newMainOffset += easedScrollDistance;
                } else {
                    // We're past our deceleration range - remove the added
                    // scroll distance that accommodated that range
                    if (scrollLockRange.decelerateDistance > 0) {
                        newMainOffset -= (
                            scrollLockAnimation.accelerationScrollDistance -
                            scrollLockAnimation.accelerationVisualDistance
                        );
                    }

                    if (scrollPosition < scrollLockRange.accelerateStart) {
                        // We're in between finishing deceleration and starting
                        // acceleration - remove the added scroll distance we've
                        // progressed through this range
                        newMainOffset -= (
                            scrollPosition -
                            scrollLockRange.decelerateEnd
                        );
                    } else {
                        // We're past the range in between finishing deceleration
                        // and starting acceleration - remove the added scroll distance
                        // that accommodated that range
                        newMainOffset -= (
                            scrollLockRange.accelerateStart -
                            scrollLockRange.decelerateEnd
                        );

                        if (scrollPosition < scrollLockRange.accelerateEnd) {
                            // We're in acceleration range - adjust the <main> offset
                            // to reflect how far we've progressed through it, starting
                            // with removing the raw scroll distance into this range
                            const rawScrollDistance =
                                scrollPosition -
                                scrollLockRange.accelerateStart;

                            newMainOffset -= rawScrollDistance;

                            // Now add back the eased distance
                            const percentComplete =
                                rawScrollDistance /
                                scrollLockRange.accelerateDistance;

                            const easedPercentComplete = scrollLockAnimation
                                .accelerateFunction(percentComplete);

                            const easedScrollDistance =
                                scrollLockAnimation.accelerationVisualDistance *
                                easedPercentComplete;

                            newMainOffset += easedScrollDistance;
                        } else {
                            // We're past our acceleration range - remove the added
                            // scroll distance that accommodated that range
                            if (scrollLockRange.accelerateDistance > 0) {
                                newMainOffset -= (
                                    scrollLockAnimation
                                        .accelerationScrollDistance -
                                    scrollLockAnimation
                                        .accelerationVisualDistance
                                );
                            }
                        }
                    }
                }

                // Similarly, determine what our animation progress should be
                let newProgress;
                if (scrollPosition < scrollLockRange.animationStart) {
                    // We haven't yet reached our animation range
                    newProgress = 0;
                } else if (scrollPosition < scrollLockRange.animationEnd) {
                    // We're in our animation range
                    newProgress =
                        (scrollPosition - scrollLockRange.animationStart) /
                        scrollLockRange.animationDistance;
                } else {
                    // We're past the end of our animation range
                    newProgress = 1;
                }

                // Update the animation
                scrollLockAnimation.setAnimationProgress(newProgress);
            });

            // Move the <main> container to reflect the scroll position
            newMainOffset = Math.round(newMainOffset);
            if (newMainOffset != mainOffset) {
                mainOffset = newMainOffset;
                mainContainer.style.setProperty(
                    settings.cssProperties.mainOffset,
                    `${-mainOffset}px`,
                );
            }

            // Notify any observers of the scroll position
            scrollPositionChangeCallbacks.forEach((callback) => {
                callback(mainOffset);
            });
        }

        // Request next animation frame
        window.requestAnimationFrame(tick);
    };

    // Setup monitoring for changes in element focus
    let lastFocusedElement = null;
    const monitorFocusChanges = () => {
        // Note that the focused element doesn't change until the "keyup" event
        document.addEventListener('keyup', (event) => {
            if (event.key != 'Tab') {
                return;
            }

            // Check if there's been a change to the focused element
            if (document.activeElement == lastFocusedElement) {
                return;
            }

            lastFocusedElement = document.activeElement;

            // Skip elements in our omit list
            const skip = settings
                .focusMonitoring
                .skipTagNames
                .includes(lastFocusedElement.tagName);

            if (skip) {
                return;
            }

            // Check the element's positioning relative to the viewport
            const bounds = lastFocusedElement.getBoundingClientRect();
            let scrollAdjustment;
            if (bounds.top < 0) {
                // Element is above the viewport
                scrollAdjustment = -1 *
                    (
                        Header.current.container.clientHeight +
                        settings
                            .focusMonitoring
                            .scrollPadding
                    );
            } else if (bounds.bottom > window.innerHeight) {
                // Element is below the viewport
                scrollAdjustment = bounds.height +
                    settings
                        .focusMonitoring
                        .scrollPadding -
                    window.innerHeight;
            } else {
                // Element is in the viewport
                return;
            }

            const scrollTarget = ScrollLockAnimation
                .getScrollTargetForElement(lastFocusedElement);

            if (!scrollTarget) {
                return;
            }

            // If a jump value was provided, first scroll to that position immediately -
            // this allows us to avoid scrolling through hero animations
            if (scrollTarget.jump > 0) {
                if (scrollTarget.jump > window.pageYOffset) {
                    window.scrollTo({
                        left: 0,
                        top: scrollTarget.jump,
                        behavior: 'auto',
                    });
                }
            }

            // Make sure header is visible if scrolling upwards
            const adjustedScrollTarget = (
                scrollTarget.position +
                scrollAdjustment
            );

            if (adjustedScrollTarget < window.pageYOffset) {
                Header.current.setVisibility(true);
            }

            window.scrollTo({
                left: 0,
                top: adjustedScrollTarget,
                behavior: 'smooth',
            });
        });
    };

    // Start loop on the next thread execution - this gives components time
    // to finish initializing anything needed during the first tick call
    window.requestAnimationFrame(() => {
        tick();
        monitorFocusChanges();
    });
}

/**
 * Determines the range this element's container spans within <main> and
 * the scroll positions in which its scroll locking takes effect
 * @this ScrollLockAnimation
 * @param {object} mainContainerBounds
 * @param {number} accumulatedScrollDistance
 */
function calculateRanges(
    mainContainerBounds,
    accumulatedScrollDistance,
) {
    const scrollLockContainerBounds =
        this.container.getBoundingClientRect();
    const scrollLockContainerTop =
        scrollLockContainerBounds.top -
        mainContainerBounds.top;

    // Determine our container's position within <main>, ignoring
    // scroll distance adjustments
    this.mainContainerRange.top = scrollLockContainerTop;
    this.mainContainerRange.bottom = this.mainContainerRange.top +
        this.container.clientHeight;

    // Determine the points in the scroll range where our scroll
    // locking events occur, starting by initiating the animation
    // at its natural location on the page
    const scrollLockRange = this.scrollLockRange;
    scrollLockRange.animationStart = scrollLockContainerTop +
        accumulatedScrollDistance;

    // If acceleration has been enabled, our deceleration range will
    // start the specified number of pixels prior to the animation's
    // start position
    scrollLockRange.decelerateStart = scrollLockRange.animationStart -
        this.accelerationVisualDistance;

    // Check that the container's position on the page can accommodate
    // the calculated deceleration start position
    if (scrollLockRange.decelerateStart < 0) {
        // This element is too high on the page for deceleration to work -
        // disable deceleration
        scrollLockRange.decelerateStart = scrollLockRange.animationStart;
        scrollLockRange.decelerateEnd = scrollLockRange.animationStart;
        scrollLockRange.decelerateDistance = 0;
    } else {
        // Either no deceleration is being performed, or there is enough
        // space above this component to perform it - calculate the end
        // point of the deceleration
        scrollLockRange.decelerateEnd = scrollLockRange.decelerateStart +
            this.accelerationScrollDistance;
        scrollLockRange.decelerateDistance = this.accelerationScrollDistance;

        // Overlap the start of the animation per our configuration
        scrollLockRange.animationStart =
            scrollLockRange.decelerateEnd -
            (
                this.parameters.decelerationOverlap *
                scrollLockRange.decelerateDistance
            );
    }

    // Determine the end position of the animation
    scrollLockRange.animationEnd = scrollLockRange.animationStart +
        scrollLockRange.animationDistance;

    // Determine the acceleration start position
    scrollLockRange.accelerateDistance = this.accelerationScrollDistance;
    scrollLockRange.accelerateStart =
        scrollLockRange.animationEnd -
        (
            this.parameters.accelerationOverlap *
            scrollLockRange.accelerateDistance
        );

    scrollLockRange.accelerateEnd = scrollLockRange.accelerateStart +
        this.accelerationScrollDistance;

    // Determine the total amount of additional scroll distance added
    // to accommodate animations
    scrollLockRange.totalScrollAdjustments = scrollLockRange.animationDistance;

    if (scrollLockRange.decelerateDistance > 0) {
        scrollLockRange.totalScrollAdjustments +=
        (
            scrollLockRange.decelerateDistance *
            (1 - this.parameters.decelerationOverlap)
        ) -
        this.accelerationVisualDistance;
    }

    if (scrollLockRange.accelerateDistance > 0) {
        scrollLockRange.totalScrollAdjustments +=
        (
            scrollLockRange.accelerateDistance *
            (1 - this.parameters.accelerationOverlap)
        ) -
        this.accelerationVisualDistance;
    }

    // Check if center pinning was requested
    if (this.parameters.pinning == 'center') {
        // Check that the component isn't at the top of the page -
        // using "1" in case there are fractional values around 0
        if (scrollLockRange.animationStart > 1) {
            // Check if this component fits on screen vertically
            const pinningOffset = (
                window.innerHeight -
                this
                    .container
                    .clientHeight
            ) / 2;

            // Same note about "1" here
            if (pinningOffset > 1) {
                // Looks like this will work
                scrollLockRange.decelerateStart -= pinningOffset;
                scrollLockRange.decelerateEnd -= pinningOffset;

                scrollLockRange.animationStart -= pinningOffset;
                scrollLockRange.animationEnd -= pinningOffset;

                scrollLockRange.accelerateStart -= pinningOffset;
                scrollLockRange.accelerateEnd -= pinningOffset;
            }
        }
    }
}

/**
 * @this {ScrollLockAnimation}
 * @param {number} value
 * @return {number}
 */
function applyParameters(value) {
    // Apply easing
    value = this.cssEasingFunction(value);

    return value;
}
